export type AppealStatus =
  | 'pending'
  | 'awaiting_staff'
  | 'staff_answered'
  | 'in_jury'
  | 'jury_decided'
  | 'approved'
  | 'denied'
  | 'escalated'
  | 'cancelled';

export const APPEAL_STATUSES: AppealStatus[] = [
  'pending', 'awaiting_staff', 'staff_answered', 'in_jury',
  'jury_decided', 'approved', 'denied', 'escalated', 'cancelled',
];

export const APPEAL_ACTIVE_STATUSES: AppealStatus[] = [
  'pending', 'awaiting_staff', 'staff_answered', 'in_jury', 'jury_decided',
];

export const APPEAL_TERMINAL_STATUSES: AppealStatus[] = [
  'approved', 'denied', 'cancelled',
];

// ---------------------------------------------------------------------------
// Jury V2 (two-stage voting) types
//
// V2 replaces the single guilty/not_guilty jury vote with a two-stage vote:
//   Stage 1: should we keep the ban as-is?         -> uphold | vary
//   Stage 2 (only if stage 1 majority is `vary`):  -> remove | reduce | increase
//
// Every appeal carries `appeal_version` (1 = legacy, 2 = two-stage). All V2
// fields are typed optional + nullable so past/in-flight V1 appeals (which
// return null/0/1 for them) and responses from older backends parse fine.
// ---------------------------------------------------------------------------

export type AppealVersion = 1 | 2;
export type V2JuryStage = 1 | 2;
export type V2Stage1Result = 'upheld' | 'varied';
export type V2Stage2Result = 'remove' | 'reduce' | 'increase' | 'made_unappealable';
export type V2StageVoteChoice = 'uphold' | 'vary' | 'remove' | 'reduce' | 'increase';

export interface V2JuryStageVote {
  id: string;
  appeal_id: number;
  juror_discord_id: string;
  juror_minecraft_uuid: string | null;
  stage: V2JuryStage;
  vote: V2StageVoteChoice | null; // null = not yet voted
  selected_at: number;            // ms epoch
  voted_at: number | null;        // ms epoch
}

export interface JuryStageVotes {
  stage1: V2JuryStageVote[];
  stage2: V2JuryStageVote[];
}

export interface AppealListItem {
  id: number;
  ban_id: number;
  user_uuid: string;
  username: string;
  status: AppealStatus;
  created_at: number;
  assigned_staff_id: string | null;
  assigned_at: number | null;
  staff_answered_at: number | null;
  decided_at: number | null;
  verdict: 'upheld' | 'overturned' | null;
  updated_at: number | null;
  ban_reason: string;
  ban_scope: string;
  ban_scope_target: string | null;
  ban_created: number;
  ban_expires: number | null;
  ban_enforced: number;

  // Jury V2 — null / absent for V1 appeals
  appeal_version?: AppealVersion;
  v2_jury_stage?: V2JuryStage | null;          // null when not in V2 jury phase
  v2_stage1_result?: V2Stage1Result | null;
  v2_stage2_result?: V2Stage2Result | null;
}

export interface AppealDetail {
  id: number;
  ban_id: number;
  user_uuid: string;
  username: string;
  contact_email: string;
  ip_address: string;
  created_at: number;
  status: AppealStatus;

  q_understanding: string | null;
  q_acknowledge: string | null;
  q_context: string | null;
  q_why: string | null;
  q_read_rules: string | null;
  q_future_steps: string | null;
  q_apology: string | null;
  q_interpretation: string | null;
  q_other: string | null;

  sq_rules_broken: string | null;
  sq_evidence: string | null;
  sq_prior_offenses: string | null;
  sq_severity: string | null;
  sq_community_harm: string | null;
  sq_warning_given: string | null;
  sq_user_response: string | null;
  sq_staff_interpretation: string | null;
  sq_duration_reasoning: string | null;
  sq_jury_exclusions: string | null;

  assigned_staff_id: string | null;
  assigned_at: number | null;
  staff_answered_at: number | null;

  ban_reason: string;
  ban_scope: string;
  ban_scope_target: string | null;
  ban_created: number;
  ban_expires: number | null;
  ban_enforced: number;
  ban_appealable: number;
  ban_issued_by: string;
  ban_uuid: string;

  player_discord_id: string | null;
  player_playtime: number | null;
  player_first_joined: number | null;
  player_last_joined: number | null;
  player_servers_seen: number | null;

  jury_votes: JuryVote[];
  appeal_history: AppealHistoryItem[];

  decided_at: number | null;
  verdict: 'upheld' | 'overturned' | null;
  decision_reason: string | null;
  reviewer_notes: string | null;
  updated_at: number | null;

  jury_summary?: string | null;
  jury_started_at?: number | null;

  // Jury V2 — null / 0 / absent for V1 appeals
  appeal_version?: AppealVersion;
  v2_jury_stage?: V2JuryStage | null;          // null when not in V2 jury phase
  v2_stage1_result?: V2Stage1Result | null;
  v2_stage2_result?: V2Stage2Result | null;

  v2_ban_expires_snapshot?: number | null;     // seconds-epoch (bans.expires unit) at jury-start
  v2_ban_appealable_snapshot?: number | null;  // 0|1
  v2_ban_enforced_snapshot?: number | null;    // 0|1

  v2_new_ban_expires?: number | null;          // the new bans.expires applied; null if not changed
  v2_ban_made_unappealable?: 0 | 1;            // 1 if backend flipped appealable 1 -> 0
  v2_proposed_reduce_seconds?: number | null;  // raw bot input — preserved even if backend clamped
  v2_proposed_increase_seconds?: number | null; // raw bot input — preserved even when ignored

  jury_stage_votes?: JuryStageVotes;           // populated for V2 appeals
  jury_exclusion_check?: {
    status: 'checked' | 'pending' | 'unavailable';
    automatic_count: number;
    checked_at: number | null;
  };
}

export interface JuryVote {
  id: string;
  appeal_id: number;
  juror_discord_id: string;
  juror_minecraft_uuid: string | null;
  vote: 'guilty' | 'not_guilty' | null;
  voted_at: number | null;
  selected_at: number;
}

export interface AppealHistoryItem {
  id: number;
  status: AppealStatus;
  created_at: number;
  decided_at?: number | null;
  decision_reason?: string | null;
  verdict?: string | null;
}

export interface AppealStats {
  total: number;
  by_status: Record<string, number>;
  pending_count: number;
  awaiting_staff_count: number;
  in_jury_count: number;
  approval_rate: number | null;
  avg_decision_time_ms: number | null;

  // Jury V2 buckets — optional so older backend responses still parse
  by_version?: { v1: number; v2: number };
  by_v2_outcome?: {
    upheld: number;
    removed: number;
    reduced: number;
    increased: number;
    made_unappealable: number;
  };
  in_jury_stage_1_count?: number;
  in_jury_stage_2_count?: number;
}

export interface AppealsByPlayer {
  id: number;
  ban_id: number;
  username: string;
  status: AppealStatus;
  created_at: number;
  decided_at: number | null;
  verdict: string | null;
  decision_reason: string | null;
  ban_reason: string;
  ban_scope: string;

  // Jury V2 — null / absent for V1 appeals
  appeal_version?: AppealVersion;
  v2_stage1_result?: V2Stage1Result | null;
  v2_stage2_result?: V2Stage2Result | null;
}

export interface AppealsByBan {
  id: number;
  status: AppealStatus;
  created_at: number;
  decided_at: number | null;
  decision_reason: string | null;
  verdict: string | null;

  // Jury V2 — null / absent for V1 appeals
  appeal_version?: AppealVersion;
  v2_stage1_result?: V2Stage1Result | null;
  v2_stage2_result?: V2Stage2Result | null;
}

export interface PaginatedAppealsResponse {
  page: number;
  limit: number;
  total: number;
  results: AppealListItem[];
}

export interface StaffAnswersRequest {
  sq_rules_broken: string;
  sq_evidence: string;
  sq_prior_offenses: string;
  sq_severity: string;
  sq_community_harm: string;
  sq_warning_given: string;
  sq_user_response: string;
  sq_staff_interpretation: string;
  sq_duration_reasoning: string;
  sq_jury_exclusions: string;
}

export interface AppealFilters {
  status?: AppealStatus | '';
  assigned_to?: string;
  user_uuid?: string;
  ban_id?: number | '';
  sort_by?: 'created_at' | 'status' | 'updated_at' | '';
  sort_order?: 'asc' | 'desc' | '';
  page?: number;
  limit?: number;
}

export const STAFF_QUESTIONS: { field: keyof StaffAnswersRequest; label: string; description: string }[] = [
  { field: 'sq_rules_broken', label: 'Rules Broken', description: 'What specific rule(s) did the user break?' },
  { field: 'sq_evidence', label: 'Evidence', description: 'Text-based evidence of the rule-breaking behavior?' },
  { field: 'sq_prior_offenses', label: 'Prior Offenses', description: 'First offense? Previous offense history?' },
  { field: 'sq_severity', label: 'Severity', description: 'Severity of the offense (minor/moderate/severe)?' },
  { field: 'sq_community_harm', label: 'Community Harm', description: 'Harm or disruption caused to the community?' },
  { field: 'sq_warning_given', label: 'Warning Given', description: 'Was any warning given prior to the ban?' },
  { field: 'sq_user_response', label: 'User Response', description: 'How did the user respond when confronted?' },
  { field: 'sq_staff_interpretation', label: 'Staff Interpretation', description: "Staff's interpretation of events leading to ban?" },
  { field: 'sq_duration_reasoning', label: 'Duration Reasoning', description: 'Ban duration and why that duration was chosen?' },
  { field: 'sq_jury_exclusions', label: 'Additional Jury Exclusions', description: 'Optional comma-separated Discord IDs. IdentSure automatically excludes linked accounts in the player’s likely circles and observed teams before jury selection.' },
];

export const PLAYER_QUESTIONS: { field: string; label: string }[] = [
  { field: 'q_understanding', label: 'Understanding of the Ban' },
  { field: 'q_acknowledge', label: 'Acknowledgement' },
  { field: 'q_context', label: 'Context' },
  { field: 'q_why', label: 'Why It Happened' },
  { field: 'q_read_rules', label: 'Rules Read' },
  { field: 'q_future_steps', label: 'Future Steps' },
  { field: 'q_apology', label: 'Apology' },
  { field: 'q_interpretation', label: 'Interpretation' },
  { field: 'q_other', label: 'Other Information' },
];
